import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Close,
  Search,
  Delete,
  CheckCircle,
  LocationOn,
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface NonServicedZip {
  _id: string;
  zipCode: string;
  requestCount: number;
  firstRequested: string;
  lastRequested: string;
  requests: Array<{ timestamp: string }>;
}

interface NonServicedZipDialogProps {
  open: boolean;
  onClose: () => void;
}

const NonServicedZipDialog: React.FC<NonServicedZipDialogProps> = ({ open, onClose }) => {
  const [nonServicedZips, setNonServicedZips] = useState<NonServicedZip[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredZips, setFilteredZips] = useState<NonServicedZip[]>([]);

  useEffect(() => {
    if (open) {
      fetchNonServicedZips();
    }
  }, [open]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredZips(nonServicedZips);
    } else {
      const filtered = nonServicedZips.filter(zip => 
        zip.zipCode.includes(searchTerm.trim())
      );
      setFilteredZips(filtered);
    }
  }, [searchTerm, nonServicedZips]);

  const fetchNonServicedZips = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching non-serviced zips...');
      const response = await apiService.get('/admin/non-serviced-zips');
      
      console.log('Full response:', response);
      console.log('Success response:', response.data);
      
      const responseData = response.data as any;
      console.log('Response data type:', typeof responseData);
      console.log('Response data is array:', Array.isArray(responseData));
      
      // Handle both direct array response and wrapped response
      if (Array.isArray(responseData)) {
        // Direct array response
        setNonServicedZips(responseData);
      } else if (responseData && responseData.success) {
        // Wrapped response with success flag
        setNonServicedZips(responseData.data || []);
      } else {
        setError(responseData.error || 'Failed to load non-serviced zip codes');
      }
    } catch (err: any) {
      console.error('Fetch error:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log out and log in again as admin.');
      } else if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to load non-serviced zip codes');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveZip = async (zipId: string) => {
    try {
      const response = await apiService.delete(`/admin/non-serviced-zips/${zipId}`);
      if (!response.success) {
        throw new Error(response.error || 'Failed to remove zip code');
      }
      setNonServicedZips(prev => prev.filter(zip => zip._id !== zipId));
      setFilteredZips(prev => prev.filter(zip => zip._id !== zipId));
    } catch (err: any) {
      console.error('Failed to remove zip code:', err);
      setError(err.message || 'Failed to remove zip code');
    }
  };

  const handleClearAll = async () => {
    try {
      const response = await apiService.delete('/admin/non-serviced-zips');
      if (!response.success) {
        throw new Error(response.error || 'Failed to clear non-serviced zip codes');
      }
      setNonServicedZips([]);
      setFilteredZips([]);
    } catch (err: any) {
      console.error('Failed to clear zip codes:', err);
      setError(err.message || 'Failed to clear non-serviced zip codes');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRequestCountColor = (count: number) => {
    if (count >= 10) return 'error';
    if (count >= 5) return 'warning';
    return 'info';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn color="primary" />
            <Typography variant="h6" component="div">
              Non-Service Area Zip Code Searches
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {nonServicedZips.length > 0 && (
              <Button size="small" onClick={handleClearAll}>
                Clear All
              </Button>
            )}
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Search and Filter */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Search by zip code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: 400 }}
              />
            </Box>

            {/* Summary Stats */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip 
                label={`Total: ${nonServicedZips.length}`} 
                color="primary" 
                variant="outlined" 
              />
              <Chip 
                label={`High Demand (10+): ${nonServicedZips.filter(zip => zip.requestCount >= 10).length}`} 
                color="error" 
                variant="outlined" 
              />
              <Chip 
                label={`Medium Demand (5-9): ${nonServicedZips.filter(zip => zip.requestCount >= 5 && zip.requestCount < 10).length}`} 
                color="warning" 
                variant="outlined" 
              />
            </Box>

            {/* Zip Codes Table */}
            {filteredZips.length === 0 ? (
              <Alert severity="info">
                {searchTerm ? 'No zip codes match your search.' : 'No non-serviced zip codes found.'}
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Zip Code</TableCell>
                      <TableCell align="center">Search Count</TableCell>
                      <TableCell align="center">First Requested</TableCell>
                      <TableCell align="center">Last Requested</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredZips.map((zip) => (
                      <TableRow key={zip._id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {zip.zipCode}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            variant="body1"
                            sx={{ 
                              fontWeight: 'bold',
                              color: zip.requestCount >= 10 ? 'error.main' : 
                                     zip.requestCount >= 5 ? 'warning.main' : 'text.primary'
                            }}
                          >
                            {zip.requestCount}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(zip.firstRequested)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(zip.lastRequested)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="Mark as serviced (keep on list)">
                              <IconButton 
                                size="small" 
                                color="success"
                                onClick={() => {
                                  // In a real implementation, you'd mark this as serviced but keep it on the list
                                  console.log('Mark as serviced:', zip.zipCode);
                                }}
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove from list">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleRemoveZip(zip._id)}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        <Button 
          onClick={fetchNonServicedZips} 
          variant="contained"
          disabled={loading}
        >
          Refresh
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NonServicedZipDialog;
